import axios, { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { UserProgress, SetUserProgressRequest, GetProgressApiResponse } from '../types/progress';
import { GradeAnalytics } from '../api/admin';
import { Fact } from '../components/Learn/types';
import { ProgressAssessment, ProgressAssessmentResponse } from '../types/progressAssessment';
import { PreKAssessmentResult } from '../components/PreKAssessment/types';
import { DEFAULT_TRACK } from '../types/constants';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from '../context/AuthContext';
import { logError } from '../utils/errorReporting';
import { IepTimerMode } from '../types/accommodations';

// Helper function to get active track ID with fallback
export const getActiveTrackId = (): string => {
  return sessionStorage.getItem('activeTrackId') || DEFAULT_TRACK;
};

// Add type augmentation for Axios config to support metadata
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    metadata?: {
      startTime: number;
    };
  }
}

interface AuthPayload {
    email: string;
    password: string;
    name?: string;
    ageGrade?: number;
}

interface AuthResponse {
    token: string;
    user?: {
        email: string;
    };
    trackId?: string;
    takeAssessment?: boolean;
}

interface ValidationResponse {
    valid: boolean;
    token?: string;
    user?: {
        email: string;
        role?: 'admin' | 'student'; // User role
        iepAccommodationsEnabled?: boolean;
        iepTimerMode?: IepTimerMode;
    };
    message?: string;
}

export class TokenValidationUnavailableError extends Error {
    readonly status?: number;

    constructor(status?: number) {
        super('Token validation temporarily unavailable');
        this.name = 'TokenValidationUnavailableError';
        this.status = status;
        Object.setPrototypeOf(this, TokenValidationUnavailableError.prototype);
    }
}

interface TargetTimeResponse {
  timePerDigit: number;
  targetTime: number;
}

interface UserSearchResponse {
  users: Array<{
    userId: string;
    email: string;
  }>;
}

interface UserCQPMResponse {
  [userId: string]: {
    name: string;
    email: string;
    tracks: {
      [trackId: string]: {
        latestCQPM: number;
        changeFromAvg: number;
      }
    }
  }
}

interface ProgressMetrics {
  currentCQPM: number | null;
  changeCQPM: number | null;
  currentAccuracy: number | null;
  bestCQPM: number | null;
}

interface TypingSpeedInput {
    count: number;
    time: number;
}

interface TypingSpeedResponse {
    average: number;
    totalCount: number;
    weightedTime: number;
    updatedAt: string;
}

export interface GetSessionResponse {
  availableActivities: {
    learn?: {
      facts: Fact[];
    };
    accuracyPractice?: {
      facts: Fact[];
    };
    fluencyPractice?: {
      groupedFacts?: {
        [key: string]: Fact[];
      };
      facts?: Fact[];
      fluencyLevel?: string;
    };
    fluencyDrill?: {
      active: boolean;
    };
  };
  progressAssessment: boolean;
  dailyAssessmentCount: number; // Count of progress assessments taken today
  practiceStatus: {
    isPracticeCompleteToday: boolean;
    factsRequiringPractice: number;
  };
  assessmentStatus: {
    lastAssessmentDate: string | null;
    daysSinceLastAssessment: number;
  };
}

// Page transition types
type PageType = 'dashboard' | 'learn' | 'practice' | 'timed-practice' | 'accuracy-practice' | 'fluency-practice' | 'assessment' | 'onboarding' | 'prek-practice' | 'k-practice' | 'g1-assessment' | 'other';

// Define the FactsByStage type to match the backend
type FactsByStage = {
  learning?: string[];
  accuracyPractice?: string[];
  fluency6Practice?: string[];
  fluency3Practice?: string[];
  fluency2Practice?: string[];
  fluency1_5Practice?: string[];
  fluency1Practice?: string[];
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export const api = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    
    if (token && config.headers) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Add request start time for tracking response time
    config.metadata = { startTime: new Date().getTime() };

    return config;
}, error => {
    return Promise.reject(error);
});

api.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    (error: AxiosError) => {
        // Calculate response time for errors too
        if (error.config?.metadata?.startTime) {
            const responseTime = new Date().getTime() - error.config.metadata.startTime;
            
            // Log all errors during accuracy practice
            if (window.location.pathname.includes('accuracy-practice')) {
                console.error(`[API] Error response during accuracy practice (${responseTime}ms):`, {
                    url: error.config?.url,
                    method: error.config?.method,
                    status: error.response?.status,
                    statusText: error.response?.statusText,
                    data: error.response?.data,
                    timestamp: new Date().toISOString(),
                    onLine: navigator.onLine
                });
            }
            // Add response time to error logs
            else if (!error.response) {
                console.warn('[API] Network error:', error.message, {
                    url: error.config?.url,
                    method: error.config?.method,
                    timeoutSet: error.config?.timeout || 'default',
                    responseTime: `${responseTime}ms`,
                    timestamp: new Date().toISOString(),
                    onLine: navigator.onLine,
                    effectiveType: (navigator as any).connection?.effectiveType || 'unknown',
                    actionPath: window.location.pathname
                });
            }
        }
        
        if (error.response?.status === 401) {
            // Capture diagnostic info before logout to help debug unexpected 401s
            const token = localStorage.getItem('token');
            let tokenInfo = 'no token';
            if (token) {
                try {
                    const decoded = jwtDecode<{ exp: number }>(token);
                    const expiresAt = new Date(decoded.exp * 1000);
                    const now = new Date();
                    tokenInfo = expiresAt < now
                        ? `expired ${Math.round((now.getTime() - expiresAt.getTime()) / 60000)} mins ago`
                        : `valid, expires in ${Math.round((expiresAt.getTime() - now.getTime()) / 60000)} mins`;
                } catch {
                    tokenInfo = 'invalid/malformed token';
                }
            }

            const diagnosticContext = {
                component: 'api-interceptor',
                action: 'unexpected-401-logout',
                endpoint: error.config?.url,
                method: error.config?.method,
                responseData: error.response?.data,
                currentPath: window.location.pathname,
                tokenState: tokenInfo,
                timestamp: new Date().toISOString(),
                networkOnline: navigator.onLine,
                sessionStorageTrackId: sessionStorage.getItem('activeTrackId'),
            };

            console.error('[API] Unexpected 401 - forcing logout:', diagnosticContext);
            logError(new Error(`Unexpected 401 from ${error.config?.method} ${error.config?.url}`), diagnosticContext);

            localStorage.removeItem('token');
            window.location.href = '/login';
        }
        
        return Promise.reject(error);
    }
);

export const login = async (credentials: AuthPayload): Promise<AuthResponse> => {
    try {
        const response = await api.post('/auth/login', credentials);

        if (!response.data || !response.data.token) {
            throw new Error('No token received from server');
        }

        return response.data;
    } catch (error) {
        if (error instanceof AxiosError) {
            throw new Error(error.response?.data?.message || 'Login failed');
        }
        throw error instanceof Error ? error : new Error('Login failed');
    }
};

export const signup = async (credentials: AuthPayload): Promise<AuthResponse> => {
    const response = await api.post('/auth/signup', credentials);

    if (!response.data) {
        const error = response.data;
        throw new Error(error.message || 'Signup failed');
    }

    return response.data;
};

export const validateToken = async (token: string): Promise<ValidationResponse> => {
    try {
        const response = await api.post(
            '/auth/validate',
            {}, // Empty body
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error(`[API] Token validation failed:`,
            error instanceof Error ? error.message : 'Unknown error');
        if (axios.isAxiosError(error) && error.response) {
            console.error('[API] Error response:', error.response.status, error.response.data);
        }

        if (axios.isAxiosError(error)) {
            const status = error.response?.status;
            if (status !== 401) {
                throw new TokenValidationUnavailableError(status);
            }
        }

        return { valid: false, message: 'Token validation failed' };
    }
};

export const processMagicLinkToken = async (token: string): Promise<AuthResponse> => {
    try {
        const response = await api.post('/auth/magic-link', { token });
        return response.data;
    } catch (error) {
        console.error(`[API] Magic link token processing failed:`,
            error instanceof Error ? error.message : 'Unknown error');
        if (axios.isAxiosError(error) && error.response) {
            console.error('[API] Error response:', error.response.status, error.response.data);
            throw new Error(error.response.data?.message || 'Magic link authentication failed');
        }
        throw error instanceof Error ? error : new Error('Magic link authentication failed');
    }
};

export const setUserProgress = async (
  userId: string,
  progress: SetUserProgressRequest
): Promise<UserProgress> => {
  try {
    // Always use track ID from session storage, fallback to TRACK1
    const trackId = getActiveTrackId();
    
    // Remove trackId from progress object since it's now in the URL
    const { trackId: _, ...progressWithoutTrackId } = progress;
    
    // Get user timezone and current date
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const currentDate = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format

    const response = await api.post(`/users/${userId}/progress/${trackId}`, progressWithoutTrackId, {
      headers: {
        'X-User-Timezone': userTimezone,
        'X-User-Date': currentDate,
      }
    });
    return response.data;
  } catch (error) {
    throw error instanceof Error ? error : new Error('Failed to set user progress');
  }
};

export const getUserProgress = async (userId: string, activeTrackId?: string): Promise<GetProgressApiResponse> => {
  try {
    const params = activeTrackId ? { activeTrackId } : {};
    const response = await api.get(`/users/${userId}/progress`, { params });
    if (!response.data) {
      throw new Error('No progress data received');
    }
    return response.data;
  } catch (error) {
    throw error instanceof Error ? error : new Error('Failed to fetch user progress');
  }
};

export const getTrackFacts = async (trackId?: string): Promise<Fact[]> => {
  try {
    // Use provided trackId or get from sessionStorage
    const currentTrackId = trackId || getActiveTrackId();
    
    const response = await api.get(`/trackFacts/track/${currentTrackId}`);
    
    if (!response.data?.success) {
      throw new Error('Failed to fetch track facts');
    }
    
    return response.data.facts;
  } catch (error) {
    throw error instanceof Error ? error : new Error('Failed to fetch track facts');
  }
};

export const getSession = async (
  userId: string, 
  activityType?: 'learn' | 'accuracyPractice' | 'fluencyPractice' | 'fluency6Practice' | 'fluency3Practice' | 'fluency2Practice' | 'fluency1_5Practice' | 'fluency1Practice' | 'all'
): Promise<GetSessionResponse> => {
  try {
    // Get the active track ID from session storage
    const trackId = getActiveTrackId();
    
    // Track missing trackId in Sentry instead of console
    if (!sessionStorage.getItem('activeTrackId')) {
      logError(new Error('Missing trackId in sessionStorage'), {
        component: 'API-getSession',
        userId,
        fallbackTrackId: 'TRACK5',
        activityType,
        url: `session/${userId}/${trackId}`,
        location: window.location.pathname
      });
    }
    
    // Get user's timezone and current date
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const userDate = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format
    
    // Call the session endpoint with timezone headers and optional activityType parameter
    const response = await api.get(`/session/${userId}/${trackId}`, {
      params: activityType ? { activityType } : {},
      headers: {
        'X-User-Timezone': userTimezone,
        'X-User-Date': userDate
      }
    });
    
    if (!response.data) {
      throw new Error('No session data received');
    }
    
    return response.data;
  } catch (error) {
    throw error instanceof Error ? error : new Error('Failed to fetch session data');
  }
};

export const getTargetTime = async (userId: string): Promise<TargetTimeResponse> => {
  try {
    const trackId = getActiveTrackId();
    const response = await api.get(`/targetTime/${userId}/${trackId}`);
    
    if (!response.data) {
      throw new Error('No target time data received');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching target time:', error);
    // Return default fallback values if the API call fails
    return {
      timePerDigit: 0.5, // Default to grade 1 typing speed
      targetTime: 3
    };
  }
};

export const searchUsers = async (email: string): Promise<UserSearchResponse> => {
  try {
    const response = await api.get(`/admin/users/search?email=${encodeURIComponent(email)}`);
    if (!response.data) {
      throw new Error('Failed to search users');
    }
    return response.data;
  } catch (error) {
    throw error instanceof Error ? error : new Error('Failed to search users');
  }
};

export const getUserCQPM = async (): Promise<UserCQPMResponse> => {
  try {
    const response = await api.get('/admin/users/cqpm');
    if (!response.data) {
      throw new Error('Failed to get user CQPM data');
    }
    return response.data;
  } catch (error) {
    throw error instanceof Error ? error : new Error('Failed to get user CQPM data');
  }
};

export const getActiveStudents = async (): Promise<any> => {
  try {
    // This endpoint will return users with their session data already attached
    // and filtered to only include those active in the last 7 days
    const response = await api.get('/admin/users/active');
    if (!response.data) {
      throw new Error('Failed to get active students data');
    }
    return response.data;
  } catch (error) {
    throw error instanceof Error ? error : new Error('Failed to get active students data');
  }
};




export const createProgressAssessment = async (
  userId: string,
  duration: number,
  trackId: string
): Promise<string> => {
  try {
    // Get user timezone and current date
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const currentDate = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format
    
    const response = await api.post(`/progressAssessment/create/${userId}`, {
      trackId,
      duration
    }, {
      headers: {
        'X-User-Timezone': userTimezone,
        'X-User-Date': currentDate,
      }
    });

    if (!response.data?.success) {
      throw new Error('Failed to create progress assessment');
    }

    return response.data.assessmentId;
  } catch (error) {
    throw error instanceof Error ? error : new Error('Failed to create progress assessment');
  }
};

export const startProgressAssessment = async (
  userId: string
): Promise<ProgressAssessmentResponse> => {
  try {
    const trackId = getActiveTrackId();
    
    // Get user timezone and current date
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const currentDate = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format
    
    const response = await api.post(`/progressAssessment/start/${userId}/${trackId}`, {}, {
      headers: {
        'X-User-Timezone': userTimezone,
        'X-User-Date': currentDate,
      }
    });

    if (!response.data?.success) {
      throw new Error('Failed to start progress assessment');
    }

    return response.data;
  } catch (error) {
    throw error instanceof Error ? error : new Error('Failed to start progress assessment');
  }
};

/**
 * Start K Accuracy Assessment
 * Fetches 20 questions from TRACK12 with specific difficulty mix
 */
export const startKAccuracyAssessment = async (
  userId: string
): Promise<ProgressAssessmentResponse> => {
  try {
    // Get user timezone and current date
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const currentDate = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format

    const response = await api.post(`/progressAssessment/k-accuracy/start/${userId}`, {}, {
      headers: {
        'X-User-Timezone': userTimezone,
        'X-User-Date': currentDate,
      }
    });

    if (!response.data?.success) {
      throw new Error('Failed to start K accuracy assessment');
    }

    return {
      assessmentId: response.data.assessmentId,
      questions: response.data.questions
    };
  } catch (error) {
    console.error('Error starting K accuracy assessment:', error);
    throw error instanceof Error ? error : new Error('Failed to start K accuracy assessment');
  }
};

export const updateProgressAssessment = async (
  userId: string,
  assessmentId: string,
  facts: { [factId: string]: { attempts: number; correct: number; timeSpent: number } }
): Promise<ProgressAssessment> => {
  try {
    const trackId = getActiveTrackId();
    
    // Get user timezone and current date
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const currentDate = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format
    
    const response = await api.post(
      `/progressAssessment/${assessmentId}/update/${trackId}/${userId}`,
      { facts },
      {
        headers: {
          'X-User-Timezone': userTimezone,
          'X-User-Date': currentDate,
        }
      }
    );

    if (!response.data?.success) {
      throw new Error('Failed to update progress assessment');
    }

    return response.data.assessment;
  } catch (error) {
    throw error instanceof Error ? error : new Error('Failed to update progress assessment');
  }
};

export const completeProgressAssessment = async (assessmentId: string, userId: string, timingData?: {
  totalTypingTimeDeducted: number;
  totalTransitionTime: number;
  actualDurationMinutes?: number;
  testType?: 'TotalTimer' | 'QuestionTimer' | 'AccuracyAssessment';
  clientSideStats?: {
    totalAttempts: number;
    totalCorrect: number;
  };
}): Promise<any> => {
  try {
    // Get user timezone and current date
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const currentDate = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format
    
    const response = await api.post(`/progressAssessment/${assessmentId}/complete/${userId}`, timingData, {
      headers: {
        'X-User-Timezone': userTimezone,
        'X-User-Date': currentDate,
      }
    });
    
    if (!response.data) {
      throw new Error('Invalid response from server');
    }
    
    return response.data.assessment;
  } catch (error) {
    console.error('Failed to complete progress assessment:', error);
    throw error;
  }
};


export const getProgressMetrics = async (userId: string): Promise<ProgressMetrics> => {
  try {
    // Get the active track ID from session storage
    const trackId = getActiveTrackId();

    // Track missing trackId in Sentry instead of console
    if (!sessionStorage.getItem('activeTrackId')) {
      logError(new Error('Missing trackId in sessionStorage'), {
        component: 'API-getProgressMetrics',
        userId,
        fallbackTrackId: 'TRACK1',
        location: window.location.pathname
      });
    }
    
    const response = await api.get(`/progressAssessment/${userId}/metrics`, {
      params: { trackId }
    });
    
    if (!response.data?.success) {
      throw new Error('Failed to fetch progress metrics');
    }
    
    return response.data.metrics;
  } catch (error) {
    throw error instanceof Error ? error : new Error('Failed to fetch progress metrics');
  }
};

export const getFluencyPracticeMetrics = async (userId: string) => {
  const trackId = getActiveTrackId();
  const response = await api.get(`/fluency-practice/${userId}/metrics`, {
    params: { trackId }
  });
  return response.data.metrics; // { bestStreak: number | null }
};

export const saveFluencyBestStreak = async (userId: string, bestStreak: number) => {
  const trackId = getActiveTrackId();
  const response = await api.put(`/fluency-practice/${userId}/metrics`, {
    bestStreak, trackId
  });
  return response.data;
};

export const updateTypingSpeed = async (
    userId: string,
    input: TypingSpeedInput
): Promise<TypingSpeedResponse> => {
    try {
        // Validate input before sending to prevent 400 errors
        if (!userId || !input || typeof input.count !== 'number' || typeof input.time !== 'number') {
            throw new Error('Invalid typing speed input data');
        }
        
        // Ensure values are positive and reasonable
        if (input.count <= 0 || input.time <= 0 || !isFinite(input.time)) {
            throw new Error(`Invalid typing speed values: count=${input.count}, time=${input.time}`);
        }
        
        const response = await api.post(`/users/${userId}/typing-speed`, input);
        
        if (!response.data) {
            throw new Error('No typing speed data received');
        }
        
        return response.data;
    } catch (error) {
        console.error(`[API] updateTypingSpeed failed:`, error instanceof Error ? error.message : 'Unknown error', 
            error instanceof AxiosError ? `status: ${error.response?.status}, data: ${JSON.stringify(error.response?.data)}` : '');
        if (error instanceof Error) {
            throw error;
        } else {
            throw new Error('Failed to update typing speed');
        }
    }
};

// Progression API types
export interface GradeBreakdown {
  grade: number;
  name: string;
  xp: number;
  cumulativeXp: number;
  isComplete: boolean;
}

export interface TrackDetail {
  trackId: string;
  name: string;
  isComplete: boolean;
  isCurrent: boolean;
}

export interface ProgressionResponse {
  currentGrade: number;
  currentGradeName: string;
  currentTrackId: string;
  todayXp: number;
  dailyXpGoal: number;
  currentGradeXp: {
    earned: number;
    total: number;
    remaining: number;
  };
  overallProgress: {
    earnedXp: number;
    totalXp: number;
    completedGrades: number[];
  };
  gradeBreakdown: GradeBreakdown[];
  currentGradeTracks: TrackDetail[];
}

export const getProgression = async (userId: string): Promise<ProgressionResponse> => {
  try {
    // Get user's timezone and current date for accurate "today's XP" calculation
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const currentDate = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format

    // Force fresh fetch - prevent all caching
    const response = await api.get(`/progression/${userId}`, {
      params: { _t: Date.now() },
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0',
        'X-User-Timezone': userTimezone,
        'X-User-Date': currentDate
      }
    });

    if (!response.data) {
      throw new Error('No progression data received');
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching progression:', error);
    // Return default fallback value if the API call fails
    return {
      currentGrade: 1,
      currentGradeName: 'Grade 1',
      currentTrackId: 'TRACK12',
      todayXp: 0,
      dailyXpGoal: 10,
      currentGradeXp: { earned: 0, total: 264, remaining: 264 },
      overallProgress: { earnedXp: 0, totalXp: 4240, completedGrades: [] },
      gradeBreakdown: [
        { grade: -1, name: 'PreK', xp: 112, cumulativeXp: 112, isComplete: false },
        { grade: 0, name: 'K', xp: 264, cumulativeXp: 376, isComplete: false },
        { grade: 1, name: 'Grade 1', xp: 264, cumulativeXp: 640, isComplete: false },
        { grade: 2, name: 'Grade 2', xp: 796, cumulativeXp: 1436, isComplete: false },
        { grade: 3, name: 'Grade 3', xp: 1188, cumulativeXp: 2624, isComplete: false },
        { grade: 4, name: 'Grade 4', xp: 916, cumulativeXp: 3540, isComplete: false },
        { grade: 5, name: 'Grade 5', xp: 700, cumulativeXp: 4240, isComplete: false }
      ],
      currentGradeTracks: []
    };
  }
};

export const recordPageTransition = async (
  data: {
    userId: string;
    trackId: string;
    page: PageType;
    factsByStage?: FactsByStage; // Add factsByStage parameter
  }
): Promise<{ success: boolean; sessionId?: string }> => {
  try {
    const response = await api.post('/sessionAnalytics/pageTransition', data);
    return response.data;
  } catch (error) {
    console.error('Error recording page transition:', error);
    return { success: false };
  }
};

export const getUserSessionAnalytics = async (userId: string): Promise<any> => {
  // TODO: Remove this function and update components that depend on session analytics
  // Session analytics has been removed - return empty data structure
  return {
    sessions: [],
    totalSessions: 0,
    totalDuration: 0,
    totalLearningTime: 0,
    totalAccuracyPracticeTime: 0,
    totalFluencyPracticeTime: 0,
    totalAssessmentTime: 0,
    lastActivity: null
  };
};

export const getUserSessionsLastWeek = async (userId: string): Promise<any> => {
  // TODO: Remove this function and update StudentData.tsx component to not depend on session data
  // Session analytics has been removed - return empty array
  return [];
};


/**
 * Updates a user's focusTrack to the next track
 * @param userId - The user's unique identifier
 * @param triggerType - Optional trigger type: 'facts_mastered' or 'cqpm_achieved'
 * @returns Object with success status and optional message
 */
export const updateUserFocusTrack = async (
  userId: string,
  triggerType?: 'facts_mastered' | 'cqpm_achieved'
): Promise<{
  success: boolean;
  message?: string;
  handoffRequested?: boolean;
  handoffStatus?: 'requested' | 'confirmed' | 'stalled';
  handoffRequestKey?: string;
  handoffDeadlineAt?: string;
}> => {
  try {
    const response = await api.put(`/users/${userId}/focus-track`, { triggerType });

    if (!response.data) {
      throw new Error('No response data received');
    }

    return response.data;
  } catch (error) {
    console.error('[API] updateUserFocusTrack failed:', error instanceof Error ? error.message : 'Unknown error',
      error instanceof AxiosError ? `status: ${error.response?.status}, data: ${JSON.stringify(error.response?.data)}` : '');
    throw error instanceof Error ? error : new Error('Failed to update user focus track');
  }
};

/**
 * Sets a user's focusTrack to a specific track
 * @param userId - The user's unique identifier
 * @param trackId - The specific track ID to set
 * @returns Object with success status and optional message
 */
export const setUserFocusTrack = async (userId: string, trackId: string): Promise<{success: boolean; message?: string}> => {
  try {
    const response = await api.put(`/users/${userId}/focus-track`, { trackId });

    if (!response.data) {
      throw new Error('No response data received');
    }

    return response.data;
  } catch (error) {
    console.error('[API] setUserFocusTrack failed:', error instanceof Error ? error.message : 'Unknown error',
      error instanceof AxiosError ? `status: ${error.response?.status}, data: ${JSON.stringify(error.response?.data)}` : '');
    throw error instanceof Error ? error : new Error('Failed to set user focus track');
  }
};

/**
 * Completes a PreK assessment and saves results to database
 * @param userId - The user's unique identifier
 * @param trackId - The PreK track ID (e.g., 'TRACK_PREK_NAMING', 'TRACK_PREK_SUBITIZING', etc.)
 * @param results - Assessment results containing totalQuestions and correctAnswers
 * @returns Promise that resolves when assessment is saved
 */

export const completePreKAssessment = async (
  userId: string,
  trackId: string,
  results: { totalQuestions: number; correctAnswers: number; activeTimeElapsed: number }
): Promise<{ assessment: PreKAssessmentResult; focusTrack?: string }> => {
  try {
    // Get user timezone and current date for consistent date handling
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const currentDate = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format

    const response = await api.post(`/progressAssessment/prek/complete/${userId}`, {
      trackId,
      ...results
    }, {
      headers: {
        'X-User-Timezone': userTimezone,
        'X-User-Date': currentDate,
      }
    });

    if (!response.data?.success) {
      throw new Error('Failed to complete PreK assessment');
    }

    return { assessment: response.data.assessment, focusTrack: response.data.focusTrack };
  } catch (error) {
    console.error(`[API] completePreKAssessment failed for track ${trackId}:`, error instanceof Error ? error.message : 'Unknown error',
      error instanceof AxiosError ? `status: ${error.response?.status}, data: ${JSON.stringify(error.response?.data)}` : '');
    throw error instanceof Error ? error : new Error('Failed to complete PreK assessment');
  }
};

/**
 * Completes a G1 Readiness Assessment and saves results to database
 * @param userId - The user's unique identifier
 * @param testType - The test type: 'NTF', 'ACRF', or 'ACCURACY'
 * @param results - Assessment results containing totalAttempted and totalCorrect
 * @returns Promise that resolves when assessment is saved
 */
export const completeG1Assessment = async (
  userId: string,
  testType: 'NTF' | 'ACRF' | 'ACCURACY',
  results: { totalAttempted: number; totalCorrect: number }
): Promise<ProgressAssessment> => {
  try {
    // Get user timezone and current date for consistent date handling
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const currentDate = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format

    const response = await api.post(`/progressAssessment/g1/complete/${userId}`, {
      testType,
      ...results
    }, {
      headers: {
        'X-User-Timezone': userTimezone,
        'X-User-Date': currentDate,
      }
    });

    if (!response.data?.success) {
      throw new Error('Failed to complete G1 assessment');
    }

    return response.data.assessment;
  } catch (error) {
    console.error(`[API] completeG1Assessment failed for test ${testType}:`, error instanceof Error ? error.message : 'Unknown error',
      error instanceof AxiosError ? `status: ${error.response?.status}, data: ${JSON.stringify(error.response?.data)}` : '');
    throw error instanceof Error ? error : new Error('Failed to complete G1 assessment');
  }
};

// Strategy-Based Learning Module APIs

export const getLearningProgress = async (userId: string): Promise<{ success: boolean; progress: any }> => {
  try {
    const response = await api.get(`/learning/${userId}/progress`);
    return response.data;
  } catch (error) {
    throw error instanceof Error ? error : new Error('Failed to fetch learning progress');
  }
};

export const updateLearningProgress = async (
  userId: string,
  data: Record<string, any>
): Promise<{ success: boolean; progress: any }> => {
  try {
    const response = await api.post(`/learning/${userId}/progress`, data);
    return response.data;
  } catch (error) {
    throw error instanceof Error ? error : new Error('Failed to update learning progress');
  }
};

export const generateTTSAudio = async (text: string): Promise<string> => {
  try {
    const response = await api.post('/tts/generate', { text });
    return response.data.audioUrl;
  } catch (error) {
    throw error instanceof Error ? error : new Error('Failed to generate TTS audio');
  }
};

export const transcribeAudio = async (audioBlob: Blob): Promise<string> => {
  const formData = new FormData();
  formData.append('audio', audioBlob);

  const response = await api.post('/speech/transcribe', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return response.data.transcript || '';
};

export const transcribeAudioForMath = async (audioBlob: Blob): Promise<string> => {
  const formData = new FormData();
  formData.append('audio', audioBlob);

  const response = await api.post('/speech/transcribe-math', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return response.data.transcript || '';
};

// ============================================================================
// Student Overview APIs
// ============================================================================

export interface StudentSearchResult {
  sourcedId: string;
  givenName: string;
  familyName: string;
  email: string;
}

export interface StudentOverviewAlert {
  type: 'LOW_CQPM_PROGRESS' | 'CONSECUTIVE_FAILS';
  triggeredDate: string;
  details: string;
  trackId: string;
}

export interface StudentOverviewGradeStats {
  grade: string;
  totalXp: number;
  avgXpPerDay: number;
  totalTime: number;
  avgTimePerDay: number;
  tracksCompleted: number;
  totalTracks: number;
}

export interface StudentOverviewAssessment {
  date: string;
  trackName: string;
  trackId: string;
  grade: number;
  cqpm: number;
  attempts: number;
  increase: number;
}

export interface StudentOverviewEnrollment {
  enrollmentId: string;
  courseId: string;
  courseTitle: string;
  beginDate?: string;
  endDate?: string;
  status: 'active' | 'ended';
}

export interface StudentOverviewData {
  email: string;
  firstName: string;
  lastName: string;
  currentTrack: string;
  currentTrackId: string;
  knowledgeGrade: string;
  enrolledGrade: number;
  currentCqpm: number;
  targetCqpm: number;
  trend: 'up' | 'down' | 'stable';
  trendValue: number;
  daysOnCurrentTrack: number;
  alerts: StudentOverviewAlert[];
  gradeStats: StudentOverviewGradeStats[];
  assessmentHistory: StudentOverviewAssessment[];
  gradeData: GradeAnalytics[];
  enrollments: StudentOverviewEnrollment[];
  oneRosterSourcedId?: string;
}

export const searchStudentsApi = async (query: string): Promise<{ users: StudentSearchResult[] }> => {
  try {
    const response = await api.get(`/admin/student-search?query=${encodeURIComponent(query)}`);
    return response.data;
  } catch (error) {
    throw error instanceof Error ? error : new Error('Failed to search students');
  }
};

export const getStudentOverviewApi = async (email: string): Promise<StudentOverviewData> => {
  try {
    const response = await api.get(`/admin/student-overview?email=${encodeURIComponent(email)}`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const messageParts = [
        error.response.data?.message,
        error.response.data?.details
      ].filter((value, index, array): value is string => Boolean(value) && array.indexOf(value) === index);

      throw new Error(messageParts.join(': ') || 'Failed to get student overview');
    }
    throw error instanceof Error ? error : new Error('Failed to get student overview');
  }
};

export interface SessionHistoryRow {
  date: string;
  trackId: string | null;
  trackName: string;
  totalTimeMinutes: number;
  activeTimeMinutes: number;
  wasteTimeMinutes: number;
  xp: number;
  assessmentCqpm: number | null;
  assessmentAccuracy: number | null;
}

export interface SessionHistoryResponse {
  rows: SessionHistoryRow[];
}

export const getStudentSessionHistoryApi = async (email: string): Promise<SessionHistoryResponse> => {
  try {
    const response = await api.get(`/admin/student-session-history?email=${encodeURIComponent(email)}`);
    return response.data;
  } catch (error) {
    throw error instanceof Error ? error : new Error('Failed to get session history');
  }
};

export const endEnrollmentApi = async (enrollmentId: string, courseId: string, oneRosterSourcedId: string): Promise<{ success: boolean }> => {
  try {
    const response = await api.post('/admin/enrollments/end', { enrollmentId, courseId, oneRosterSourcedId });
    return response.data;
  } catch (error) {
    throw error instanceof Error ? error : new Error('Failed to end enrollment');
  }
};

export const updateAdminFocusTrack = async (trackId: string) => {
  const response = await api.put('/admin/focus-track', { trackId });
  return response.data;
};
